import React, { useState, useEffect } from "react";
import { Platform, Image } from "react-native";
import i18n from "../../locales/i18n";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

import AppIcon from "../../assets/logo-icon-no-bg.png";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.mohamed_outerbah.khdame";
const SMALL_SCREEN_BREAKPOINT = 768;

const MobileWebBanner: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);
  const isRTL = locale === "ar" || i18n.locale === "ar";
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;

    const checkSize = () => {
      setIsSmallScreen(window.innerWidth < SMALL_SCREEN_BREAKPOINT);
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  // Animate in after mount
  useEffect(() => {
    if (isSmallScreen && !dismissed) {
      const timer = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isSmallScreen, dismissed]);

  // Only show on web, small screens, and not dismissed
  if (Platform.OS !== "web" || !isSmallScreen || dismissed) return null;

  // Inject keyframes once
  if (
    typeof document !== "undefined" &&
    !document.getElementById("mobile-banner-styles")
  ) {
    const style = document.createElement("style");
    style.id = "mobile-banner-styles";
    style.textContent = `
      @keyframes bannerSlideUp {
        from { opacity: 0; transform: translateY(40px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes bannerFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes bannerPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.03); }
      }
      .mobile-banner-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25) !important;
      }
      .mobile-banner-skip:hover {
        color: #555 !important;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: visible ? "rgba(0, 0, 0, 0.55)" : "rgba(0, 0, 0, 0)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        direction: isRTL ? "rtl" : "ltr",
        transition: "background-color 0.4s ease",
      }}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: 28,
          maxWidth: 360,
          width: "100%",
          textAlign: "center",
          boxShadow:
            "0 25px 80px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255,255,255,0.1)",
          position: "relative",
          overflow: "hidden",
          animation: visible
            ? "bannerSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards"
            : "none",
        }}
      >
        {/* Top gradient accent */}
        <div
          style={{
            height: 4,
            background: "linear-gradient(90deg, #0D6E8A, #1379A2, #17A5D6)",
            width: "100%",
          }}
        />

        <div style={{ padding: "28px 28px 24px" }}>
          {/* Close button */}
          <button
            onClick={() => {
              setVisible(false);
              setTimeout(() => setDismissed(true), 300);
            }}
            style={{
              position: "absolute",
              top: 14,
              right: isRTL ? "auto" : 14,
              left: isRTL ? 14 : "auto",
              background: "#F5F5F5",
              border: "none",
              fontSize: 16,
              color: "#999",
              cursor: "pointer",
              width: 32,
              height: 32,
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#E8E8E8";
              e.currentTarget.style.color = "#666";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#F5F5F5";
              e.currentTarget.style.color = "#999";
            }}
          >
            ✕
          </button>

          {/* App icon */}
          <div
            style={{
              width: 100,
              height: 100,

              borderRadius: 18,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              margin: "0 auto 16px",
            }}
          >
            <Image
              source={AppIcon}
              style={{ width: 73, height: 73 }}
              resizeMode="cover"
            />
          </div>

          {/* App name */}
          <div
            style={{
              fontSize: 13,
              fontWeight: "700",
              color: "#1379A2",
              letterSpacing: 1.5,
              textTransform: "uppercase",
              marginBottom: 6,
              fontFamily: "sans-serif",
            }}
          >
            KHDAME
          </div>

          {/* Title */}
          <h2
            style={{
              fontSize: 20,
              fontWeight: "800",
              color: "#1A1A2E",
              marginBottom: 8,
              fontFamily: "sans-serif",
              lineHeight: 1.3,
              margin: "0 0 8px",
            }}
          >
            {i18n.t("downloadApp")}
          </h2>

          {/* Message */}
          <p
            style={{
              fontSize: 14,
              color: "#777",
              lineHeight: 1.7,
              marginBottom: 24,
              fontFamily: "sans-serif",
              margin: "0 0 24px",
              padding: "0 4px",
            }}
          >
            {i18n.t("smallScreenMessage")}
          </p>

          {/* Google Play button — dark style */}
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-banner-btn"
            style={{
              display: "flex",

              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              background: "linear-gradient(135deg, #1A1A2E 0%, #2D2D44 100%)",
              color: "#FFFFFF",
              textDecoration: "none",
              padding: "14px 24px",
              borderRadius: 14,
              fontFamily: "sans-serif",
              marginBottom: 16,
              transition: "all 0.3s ease",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
              cursor: "pointer",
            }}
          >
            {/* Google Play icon */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 466 511.98"
              style={{ flexShrink: 0 }}
            >
              <path
                fill="#EA4335"
                d="M199.9 237.8l-198.5 232.37c7.22,24.57 30.16,41.81 55.8,41.81 11.16,0 20.93,-2.79 29.3,-8.37l0 0 244.16 -139.46 -130.76 -126.35z"
              />
              <path
                fill="#FBBC04"
                d="M433.91 205.1l0 0 -104.65 -60 -111.61 110.22 113.01 108.83 104.64 -58.6c18.14,-9.77 30.7,-29.3 30.7,-50.23 -1.4,-20.93 -13.95,-40.46 -32.09,-50.22z"
              />
              <path
                fill="#34A853"
                d="M199.42 273.45l129.85 -128.35 -241.37 -136.73c-8.37,-5.58 -19.54,-8.37 -30.7,-8.37 -26.5,0 -50.22,18.14 -55.8,41.86 0,0 0,0 0,0l198.02 231.59z"
              />
              <path
                fill="#4285F4"
                d="M1.39 41.86c-1.39,4.18 -1.39,9.77 -1.39,15.34l0 397.64c0,5.57 0,9.76 1.4,15.34l216.27 -214.86 -216.28 -213.46z"
              />
            </svg>
            <div style={{ textAlign: isRTL ? "right" : "left" }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: "400",
                  opacity: 0.8,
                  letterSpacing: 0.5,
                  lineHeight: 1,
                  marginBottom: 2,
                }}
              >
                {i18n.t("getItOn")}
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: "700",
                  lineHeight: 1.2,
                  letterSpacing: 0.3,
                }}
              >
                {i18n.t("googlePlay")}
              </div>
            </div>
          </a>

          {/* Continue on web link */}
          <a
            href="https://www.khdame.com/"
            className="mobile-banner-skip"
            style={{
              display: "block",
              background: "none",
              border: "none",
              color: "#AAA",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "sans-serif",
              padding: "8px 16px",
              borderRadius: 8,
              transition: "color 0.2s",
              textDecoration: "none",
            }}
          >
            {i18n.t("continueOnWeb")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileWebBanner;
